import { useEffect } from 'react'
import { isRouteErrorResponse, Link, useRouteError } from 'react-router'
import { Button } from '@/components/ui/button'
import { reportError } from '@/lib/sentry'

/** Shown when a page crashes or the address doesn't exist. Crashes are reported to Sentry. */
export function RouteErrorBoundary() {
  const error = useRouteError()
  const notFound = isRouteErrorResponse(error) && error.status === 404
  const chunkFailed =
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed/i.test(error.message)

  useEffect(() => {
    if (!notFound && !chunkFailed) reportError(error)
  }, [error, notFound, chunkFailed])

  return (
    <div className="grid min-h-dvh place-items-center bg-background px-6">
      <div role="alert" className="grid max-w-md gap-4 text-center">
        <p className="eyebrow">{notFound ? '404' : 'Unexpected error'}</p>
        <h1 className="display text-4xl text-ink">
          {notFound ? 'This page doesn’t exist' : 'Something went wrong'}
        </h1>
        <p className="text-sm text-ink-muted">
          {notFound
            ? 'Check the address, or go back to the dashboard.'
            : chunkFailed
              ? 'A new version of the control panel is available. Reload to continue.'
              : 'The page failed to load. Reloading usually fixes it.'}
        </p>
        <div>
          {notFound ? (
            <Button asChild variant="ink">
              <Link to="/">Go to the dashboard</Link>
            </Button>
          ) : (
            <Button variant="ink" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
